import {Component, ElementRef, EventEmitter, Inject, OnInit, ViewChild} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogConfig, MatDialogRef} from '@angular/material';
import {ScrollHelper} from '../../../services/scroll-helper';
import {TemplateOptions} from '../../fields/ng-multiselect/select.component';
import {DialogConfigBase} from '../dialog-config-base';
import {DialogComponentInterface} from '../dialog-component.interface';
import {DialogConfigInterface} from '../dialog-config.interface';
import * as _ from 'lodash';
import {isNotNullOrUndefined} from 'codelyzer/util/isNotNullOrUndefined';
import {SelectDialogMode} from '../../../models/select-dialog-mode.enum';

@Component({
  selector: 'app-select-dialog',
  templateUrl: './select-dialog.component.html',
  styleUrls: ['./select-dialog.component.scss']
})
export class SelectDialogComponent implements OnInit, DialogComponentInterface {

  @ViewChild('searchInput') searchInput: ElementRef;

  datepickerViewConfig: SelectDatepickerViewConfig = {
    columnsNumber: 4,
    rowsNumber: 6,
  };

  filteredItems;
  asyncItems: any[] = null;
  items: any[] = null;
  loading: boolean = false;
  noResultsText: string;

  static getConfigBuilder(): any {
    return new SelectDialogConfig();
  }

  get selectSearchText(): string {
    return this.data.fieldConfig.select.typeToSearchText ? this.data.fieldConfig.select.typeToSearchText : 'PLACEHOLDERS.search';
  }

  get mode(): SelectDialogMode {
    return isNotNullOrUndefined(this.data.fieldConfig.select.dialogMode) ? this.data.fieldConfig.select.dialogMode : SelectDialogMode.STANDARD;
  }

  get datepickerMode(): boolean {
    return this.mode === SelectDialogMode.DATEPICKER;
  }

  get standardMode(): boolean {
    return this.mode === SelectDialogMode.STANDARD;
  }

  get searchVisible(): boolean {
    return this.standardMode;
  }

  constructor(@Inject(MAT_DIALOG_DATA) public data: SelectDialogComponentData,
              private scrollHelper: ScrollHelper,
              private dialogRef: MatDialogRef<SelectDialogComponent>) {
  }

  get noResults(): boolean {
    return this.filteredItems && this.filteredItems.length === 0 && !this.loading;
  }

  ngOnInit() {
    this.init();
  }

  async init() {
    try {
      if (this.searchInput) {
        this.searchInput.nativeElement.value = '';
      }
      this.initNoResultsText();
      this.loading = true;
      this.asyncItems = await this.data.list.then(items => items);
      this.loading = false;
      this.filteredItems = this.asyncItems;
    } catch (e) {
      this.loading = false;
      this.items = <any>this.data.list;
      this.filteredItems = this.items;
    }
    this.handleItemsFromEvent();
  }

  isItemSelected(item: string | number) {
    const { item: code } = this.data.fieldConfig.options;
    if (code) {
      return item[code] === this.data.controlValue;
    } else {
      return this.data.controlValue === item;
    }
  }

  getConcatLabel(item) {
    return String(this.data.getConcatLabel(item, this.data.fieldConfig.options.label));
  }

  filterValues(phrase: string) {
    if (this.data.fieldConfig.select.dynamicDictionary) {
      this.loading = true;
    }
    this.filteredItems = (this.items || this.asyncItems)
      .filter((value) => this.getConcatLabel(value).toLowerCase().includes(phrase.toLowerCase()));
    if (this.data.fieldConfig.select.searchEvent) {
      this.data.fieldConfig.select.searchEvent.emit(phrase);
    }
  }


  closeSelect(item = null) {
    if (this.data.fieldConfig.select.sharedSelectDialog) {
      if (isNotNullOrUndefined(item) && this.data.fieldConfig.select.valueSelectedEvent) {
        this.data.fieldConfig.select.valueSelectedEvent.emit(this.getValue(item));
      }
      if (!isNotNullOrUndefined(item) || this.data.fieldConfig.select.lastFieldInSharedSelectDialog) {
        this.dialogRef.close(null);
      }
    } else {
      this.dialogRef.close(item && this.getValue(item));
      if (this.data.fieldConfig.select.closeEvent) {
        this.data.fieldConfig.select.closeEvent.emit();
      }
    }
  }

  onDatepickerItemSelected(item: any): void {
    this.closeSelect(item);
  }

  private getValue(value: any) {
    return this.data.fieldConfig.options.item ? value[this.data.fieldConfig.options.item] : value;
  }

  private handleItemsFromEvent(): void {
    if (this.data.itemsEvent) {
      if (this.filteredItems.length === 0) {
        this.loading = true;
      }
      this.data.itemsEvent.subscribe(items => {
        this.filteredItems = items;
        this.updateItems(items);
        this.loading = false;
      });
    }

    if (this.data.fieldConfig.select.dynamicDictionary && this.data.dynamicDictionaryEvent) {
      this.data.dynamicDictionaryEvent.subscribe((event: DynamicDictionaryEvent) => {
        this.filteredItems = event.items;
        this.updateItems(event.items);
        this.noResultsText = event.noResultsText ? event.noResultsText : 'SELECT_DIALOG_COMPONENT.noResults';
        this.loading = false;
      })
    }
  }

  private updateItems(items: any[]): void {
    const updatedItems = [
        ...(this.items ? this.items : []),
        ...items
    ];
    this.items = this.data.fieldConfig.options ? _.uniqBy(updatedItems, this.data.fieldConfig.options.item) : _.uniq(updatedItems);
  }

  private initNoResultsText(): void {
    if (this.data.fieldConfig.select.notFoundText) {
      this.noResultsText = this.data.fieldConfig.select.notFoundText;
    } else {
      this.noResultsText = 'SELECT_DIALOG_COMPONENT.noResults';
    }
  }

}

export class SelectDialogConfig extends DialogConfigBase implements DialogConfigInterface {
  private config: MatDialogConfig = {
    panelClass: ['select-dialog'],
    disableClose: true,
    autoFocus: false,
  };

  constructor() {
    super();
  }

  buildConfiguration(data: SelectDialogComponentData): MatDialogConfig {
    if (data.fieldConfig.select && data.fieldConfig.select.mobileSelectPanelClass) {
      (<string[]>this.config.panelClass).push(data.fieldConfig.select.mobileSelectPanelClass);
    }
    return this.buildConfig(data, this.config);
  }
}

export interface SelectDialogComponentData {
  list: Promise<any>,
  controlValue,
  fieldConfig: any,
  translateLabel: boolean,
  selectSearchVisible: boolean,
  getConcatLabel: (item, label) => string,
  templateOptions: TemplateOptions,
  itemsEvent: EventEmitter<any>,
  dynamicDictionaryEvent: EventEmitter<DynamicDictionaryEvent>,
  loadingText: string;
  additionalPanelClass?: string;
}

export interface DynamicDictionaryEvent {
  items: any[];
  noResultsText?: string;
}

export interface SelectDatepickerViewConfig {
  rowsNumber: number;
  columnsNumber: number;
}
